/**
 * LooperC Music Configuration Module
 * Contains all constants, token mappings, and standard song patterns.
 * 
 * Ported from looperc-factory/src/config.py
 */

/**
 * Genre-aware aliases for each canonical section type.
 * Mirrors the multi-token mapping in looperc-factory/src/config.py (song_type_dict):
 *   vs/ve/gr -> verse, ch -> chorus, br -> bridge, bk -> break,
 *   in -> intro, pr -> pre, en -> ending
 *
 * Used in the Upload panel to show familiar names to EDM, hip-hop, groove, and rock users.
 */
export const SECTION_ALIASES: Record<string, string[]> = {
    verse:   ['Groove', 'Riff', 'Section'],
    chorus:  ['Drop', 'Hook'],
    bridge:  ['Break', 'Buildup', 'Transition'],
    break:   ['Breakdown', 'Brkdn', 'Interlude'],
    pre:     ['Pre-Chorus', 'Buildup'],
    intro:   [],
    ending:  ['Outro'],
};


/**
 * Size category for UI bar selection
 */
export type SizeCategory = 'SHORT' | 'MID' | 'LARGE' | 'CUSTOM';

/**
 * Default parameters for music generation
 */
export const DEFAULT_PULSE_COMPAS = 4;
export const DEFAULT_TIME_REFERENCE = 60;

/**
 * Coin-change population engine parameters.
 *
 * COIN_CHANGE_TOLERANCE: maximum bar-count deviation allowed when matching
 *   a combination sum to a section target. Set to 0.15 to absorb floating-point
 *   drift in real audio durations (e.g. 3.97 bars instead of 4.00). At 120 BPM
 *   (barTime ~2 s) this equals +-0.30 s per section. Testing showed that 0.05
 *   produced zero valid combinations for float-drift albums on targets like 12 bars,
 *   while 0.15 yields 6+ combinations for the same input.
 *
 * COIN_CHANGE_MAX_COMBOS: hard cap on the number of valid combinations collected
 *   per section. 1000 is never reached in practice for typical album data
 *   (3-6 distinct bar sizes, targets <= 32 bars).
 */
export const COIN_CHANGE_TOLERANCE = 0.125;
export const COIN_CHANGE_MAX_COMBOS = 2000;

/**
 * Periodic fill parameters.
 *
 * PERIODIC_FILL_ENABLED: when true, body slots at every PERIODIC_FILL_EVERY bars
 *   are candidates for fill replacement (subject to probability gate).
 *
 * PERIODIC_FILL_EVERY: bar interval at which a body slot is a candidate for
 *   fill replacement (e.g. 4 = every 4 bars).
 *
 * PERIODIC_FILL_PROBABILITY: probability (0–1) that a candidate slot is actually
 *   replaced with a fill. Default 0.25 = 1 in 4 times.
 */
export const PERIODIC_FILL_ENABLED     = true;
export const PERIODIC_FILL_EVERY       = 4;
export const PERIODIC_FILL_PROBABILITY = 0.5;